import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { isLoaded, isEmpty } from "../../store/hooks";
import { useHistory } from "react-router-dom";
import { createMedia } from "@artsy/fresnel";
import {
  Button,
  Container,
  Grid,
  Header,
  Icon,
  Image,
  Menu,
  Segment,
  Sidebar,
  Visibility,
} from "semantic-ui-react";

import BottomSection from "../sharedComponents/BottomSection.js";
import NavigationBar from "../sharedComponents/NavigationBar.js";
import welcomePageImage from "Images/pointing_at_monitor.jpg";
import marketing from "Images/marketing.png";
import feedback from "Images/feedback.svg";

const AppMedia = createMedia({
  breakpoints: {
    mobile: 320,
    tablet: 768,
    computer: 992,
    largeScreen: 1200,
    widescreen: 1920,
  },
});
const mediaStyles = AppMedia.createMediaStyle();
const { Media, MediaContextProvider } = AppMedia;

const WelcomepageHeading = ({ mobile }) => {
  const history = useHistory();
  return (
    <Container className="welcomepage-heading" text>
      <Header
        as="h1"
        content="FELLA"
        inverted
        style={{
          fontSize: mobile ? "3em" : "4em",
          fontWeight: "bold",
          marginBottom: 0,
          marginTop: mobile ? "0.5em" : "3em",
        }}
      />
      <Header
        as="h2"
        content="Fellowship Life Starts Here"
        inverted
        style={{
          fontSize: mobile ? "1.5em" : "1.7em",
          fontWeight: "normal",
          marginTop: mobile ? "0.3em" : "0.5em",
        }}
      />
      <Header
        as="h3"
        content="Keep connected with other members and stay updated with latest news of your favourite group"
        inverted
        style={{
          fontSize: mobile ? "1.0em" : "1.0em",
          marginTop: mobile ? "0.5em" : "1.0em",
        }}
      />
      <Button
        primary
        size="huge"
        onClick={() => {
          history.push("/home");
        }}
      >
        Get Started
        <Icon name="right arrow" />
      </Button>
    </Container>
  );
};

WelcomepageHeading.propTypes = {
  mobile: PropTypes.bool,
};

const DesktopContainer = ({ children, isAuthenticated }) => {
  const [fixed, setFixed] = useState(false);

  const hideFixedMenu = () => setFixed(false);
  const showFixedMenu = () => setFixed(true);

  return (
    <Media greaterThan="mobile">
      <Visibility
        once={false}
        onBottomPassed={showFixedMenu}
        onBottomPassedReverse={hideFixedMenu}
      >
        <Segment
          inverted
          textAlign="center"
          style={{ minHeight: 700, padding: "0.1em 0em" }}
          vertical
        >
          <div
            className="welcomepage-image"
            style={{ position: "absolute", width: "100%" }}
          >
            <img src={welcomePageImage} height={700} alt="Welcome" />
          </div>
          <NavigationBar fixed={fixed} inverted={!fixed} showLogo={fixed} showActions />
          <WelcomepageHeading />
        </Segment>
      </Visibility>
      {children}
    </Media>
  );
};

DesktopContainer.propTypes = {
  children: PropTypes.node,
  isAuthenticated: PropTypes.bool,
};

const MobileContainer = ({ children }) => {
  const [sidebarOpened, setSidebarOpened] = useState(false);

  const handleSidebarHide = () => setSidebarOpened(false);
  const handleToggle = () => setSidebarOpened(true);

  return (
    <Sidebar.Pushable as={Media} at="mobile">
      <Sidebar
        as={Menu}
        animation="push"
        inverted
        onHide={handleSidebarHide}
        vertical
        visible={sidebarOpened}
      >
        <Menu.Item as="a" active>
          Home
        </Menu.Item>
        <Menu.Item as="a">Log in</Menu.Item>
        <Menu.Item as="a">Sign Up</Menu.Item>
      </Sidebar>

      <Sidebar.Pusher dimmed={sidebarOpened}>
        <Segment
          inverted
          textAlign="center"
          style={{ minHeight: 450, padding: "1em 0em" }}
          vertical
        >
          <Container>
            <Menu inverted pointing secondary size="large">
              <Menu.Item onClick={handleToggle}>
                <Icon name="sidebar" />
              </Menu.Item>
              <Menu.Item position="right">
                <Button as="a" inverted>
                  Log in
                </Button>
                <Button as="a" inverted style={{ marginLeft: "0.5em" }}>
                  Sign Up
                </Button>
              </Menu.Item>
            </Menu>
          </Container>
          <WelcomepageHeading mobile />
        </Segment>
        {children}
      </Sidebar.Pusher>
    </Sidebar.Pushable>
  );
};

MobileContainer.propTypes = {
  children: PropTypes.node,
};

const ResponsiveContainer = ({ children }) => {
  const auth = useSelector((state) => state.auth.auth);
  const isAuthenticated = isLoaded(auth) && !isEmpty(auth);

  return (
    <div>
      <DesktopContainer isAuthenticated={isAuthenticated}>
        {children}
      </DesktopContainer>
      <MobileContainer isAuthenticated={isAuthenticated}>
        {children}
      </MobileContainer>
    </div>
  );
};

ResponsiveContainer.propTypes = {
  children: PropTypes.node,
};

const WelcomePage = () => {
    const history = useHistory();
    return (
        <>
          <style>{mediaStyles}</style>
          <MediaContextProvider>
            <ResponsiveContainer>
              <Segment style={{ padding: "8em 0em" }} vertical>
                <Grid container stackable verticalAlign="middle">
                  <Grid.Row>
                    <Grid.Column width={8}>
                      <Header as="h3" style={{ fontSize: "2em" }}>
                        We Help Churches and Groups to Grow
                      </Header>
                      <p style={{ fontSize: "1.33em" }}>
                        Fella is a group management online platform for leaders to
                        administer and provide latest updates and upcoming events
                        information to members
                      </p>
                      <Header as="h3" style={{ fontSize: "2em" }}>
                        Real-Time Dashboard & Analytical Insights
                      </Header>
                      <p style={{ fontSize: "1.33em" }}>
                        We offers a wide-range of data analytical dashboards to
                        analyze group trends and provide statistics on the health of
                        your groups
                      </p>
                    </Grid.Column>
                    <Grid.Column floated="right" width={8}>
                      <Image size="massive" src={marketing} />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Segment>
              <Segment style={{ padding: "8em 0em" }} vertical>
                <Grid container stackable verticalAlign="middle">
                  <Grid.Row>
                    <Grid.Column width={8}>
                      <Image size="large" src={feedback} />
                    </Grid.Column>
                    <Grid.Column width={8}>
                      <Header as="h3" style={{ fontSize: "2em" }}>
                        Missing some features?
                      </Header>
                      <p style={{ fontSize: "1.33em" }}>
                        {
                          "Fella's mission is to offer a community platform and promote user engagement. \
                      We are here to listen and your valuable feedback is most important to us!"
                        }
                      </p>
                      <Button
                        as="a"
                        size="large"
                        onClick={() => {
                          history.push("/feedback");
                        }}
                      >
                        {"Let Us Know"}
                      </Button>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Segment>
              <BottomSection />
            </ResponsiveContainer>
          </MediaContextProvider>
        </>
      );
};

export default WelcomePage;
